/**
 * Normalizes a `keywords_query` string generated by the LLM into clean
 * PostgreSQL FTS syntax understood by FtsQueryPreprocessor on the backend.
 *
 * Transformations applied (in order):
 *  1. Strip parentheses — the backend strips them anyway and they confuse display.
 *  2. Normalize word operators AND / OR / NOT → & / | / !
 *  3. Collapse extra whitespace.
 *  4. When operators are present, auto-quote any multi-word term between them
 *     (e.g. "Russian Federation" becomes "Russian Federation") so the backend
 *     treats it as a phrase adjacency match instead of splitting into two terms.
 *
 * When no explicit operators are present the string is returned as-is; the
 * backend FtsQueryPreprocessor inserts an implicit & between bare words.
 *
 * @param {string} rawQuery  Raw keywords_query as stored in Y.Doc / returned by LLM
 * @returns {string}         Normalized FTS string ready for the search input
 */
export function normalizeKeywordsQuery(rawQuery) {
    if (!rawQuery?.trim()) return '';

    let q = rawQuery.trim();

    // 1. Remove parentheses (not supported by to_tsquery)
    q = q.replace(/[()]/g, ' ');

    // 2. Normalize word operators (must be word-boundary matches to avoid hitting
    //    words like "Anderson" or "Oregon")
    q = q
        .replace(/\bAND\b/gi, '&')
        .replace(/\bOR\b/gi, '|')
        .replace(/\bNOT\b/gi, '!');

    // 3. Collapse multiple spaces
    q = q.replace(/\s+/g, ' ').trim();

    // 4. If no explicit operators remain, return as-is — the backend adds implicit &
    const hasOperators = /[&|!]/.test(q);
    if (!hasOperators) {
        return q;
    }

    // 5. Split on operator symbols, quote any multi-word term between them
    const parts = q.split(/\s*([&|!])\s*/);
    const tokens = [];
    for (const part of parts) {
        const t = part.trim();
        if (!t) continue;
        if (t === '&' || t === '|' || t === '!') {
            tokens.push(t);
        } else if (t.includes(' ') && !t.startsWith('"')) {
            tokens.push(`"${t}"`);
        } else {
            tokens.push(t);
        }
    }

    // Drop any dangling leading/trailing operators
    const isOp = (s) => s === '&' || s === '|' || s === '!';
    while (tokens.length > 0 && isOp(tokens[0])) tokens.shift();
    while (tokens.length > 0 && isOp(tokens[tokens.length - 1])) tokens.pop();

    return tokens.join(' ');
}
